/*!
 * (C) Ionic http://ionicframework.com - MIT License
 */

import {
    ViewEncapsulation,
    Component,
    HostBinding,
    ElementRef,
    ChangeDetectionStrategy,
    inject,
    input,
    Signal,
    computed,
    OnInit,
} from '@angular/core';
import { Observable } from 'rxjs';
import { IconService } from './icon.service';
import { SizeRange } from '@bazis/shared/models/base.types';

const sharedStylesheet = new CSSStyleSheet();
sharedStylesheet.replaceSync(
    `:host { --bazis-icon-mini-size: 12px; --bazis-icon-compact-size: 16px; --bazis-icon-regular-size: 18px; --bazis-icon-large-size: 20px; --bazis-icon-huge-size: 24px; --bazis-icon-giant-size: 32px; display: inline-block; width: 1em; height: 1em; contain: strict; fill: currentColor; -webkit-box-sizing: content-box !important; box-sizing: content-box !important; font-size: var(--bazis-icon-huge-size); vertical-align: middle; } :host .bazisicon { stroke: currentColor; } :host .bazisicon.bazisicon-stroke-none { stroke: none; } :host(.bazis-icon-gradient) svg path[fill=currentColor] { fill: url(#bazisIconGradient); } :host(.bazis-flip-rtl) .bazis-icon-inner { -webkit-transform: scaleX(-1); transform: scaleX(-1); } :host(.bazis-icon-mini) { font-size: var(--bazis-icon-mini-size) !important; } :host(.bazis-icon-compact) { font-size: var(--bazis-icon-compact-size) !important; } :host(.bazis-icon-regular) { font-size: var(--bazis-icon-regular-size) !important; } :host(.bazis-icon-large) { font-size: var(--bazis-icon-large-size) !important; } :host(.bazis-icon-giant) { font-size: var(--bazis-icon-giant-size) !important; } :host(.bazis-color:not(.bazis-icon_fill)) { color: var(--bazis-color-base) !important; } :host(.bazis-icon_fill) { background: var(--bazis-color-base); color: var(--bazis-color-contrast); } :host(.bazis-color-primary) { --bazis-color-base: var(--bazis-color-primary, #00a2ad); --bazis-color-contrast: #ffffff; } :host(.bazis-color-secondary) { --bazis-color-base: var(--bazis-color-secondary, #43b1f2); --bazis-color-contrast: #ffffff; } :host(.bazis-color-tertiary) { --bazis-color-base: var(--bazis-color-tertiary, #817af9); --bazis-color-contrast: #ffffff; } :host(.bazis-color-action) { --bazis-color-base: var(--bazis-color-action, #164982); --bazis-color-contrast: #ffffff; } :host(.bazis-color-info) { --bazis-color-base: var(--bazis-color-info, #c4c4c4); --bazis-color-contrast: #ffffff; } :host(.bazis-color-success) { --bazis-color-base: var(--bazis-color-success, #78c649); --bazis-color-contrast: #ffffff; } :host(.bazis-color-warning) { --bazis-color-base: var(--bazis-color-warning, #f6bf36); --bazis-color-contrast: #ffffff; } :host(.bazis-color-danger) { --bazis-color-base: var(--bazis-color-danger, #ed1a34); --bazis-color-contrast: #ffffff; } :host(.bazis-color-light) { --bazis-color-base: var(--bazis-color-light, #f4f5f8); } :host(.bazis-color-medium) { --bazis-color-base: var(--bazis-color-medium, #989aa2); } :host(.bazis-color-dark) { --bazis-color-base: var(--bazis-color-dark, #111214); } .bazisicon-fill-none { fill: none; } .bazisicon-stroke-width { stroke-width: 32px; stroke-width: var(--bazisicon-stroke-width, 32px); } .bazis-icon-inner, .bazisicon, svg { display: block; height: 100%; width: 100%; pointer-events: none; }`,
);

@Component({
    selector: 'bazis-icon',
    template: ` @let svgIcon = svgContent$() | async;
        @if (svgIcon) {
            <div
                [innerHTML]="svgIcon"
                class="bazis-icon-inner"
            ></div>
        }
        @if (useGradient()) {
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
            >
                <defs>
                    <linearGradient
                        id="bazisIconGradient"
                        gradientUnits="userSpaceOnUse"
                        [attr.x1]="gradientOptions().x1"
                        [attr.x2]="gradientOptions().x2"
                        [attr.y1]="gradientOptions().y1"
                        [attr.y2]="gradientOptions().y2"
                    >
                        <stop stop-color="var(--bazis-icon-stop-start)" />
                        <stop
                            offset="1"
                            stop-color="var(--bazis-icon-stop-end)"
                        />
                    </linearGradient>
                </defs>
            </svg>
        }`,
    // styleUrls: ['icon.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IconElement implements OnInit {
    public el = inject(ElementRef);

    private iconService = inject(IconService);

    readonly name = input<string>();

    readonly src = input<string>();

    readonly color = input<string>();

    readonly icon = input<string>();

    readonly useGradient = input<boolean>();

    readonly size = input<SizeRange>(SizeRange.huge);
    //                       mini  compact regular large huge  giant
    // readonly size = input<'xxs' | 'xs' | 's' | 'md' | 'l' | 'xl'>('l');

    readonly gradientOptions = input<{
        x1: number;
        y1: number;
        x2: number;
        y2: number;
    }>();

    @HostBinding('role') get roleElement() {
        return 'img';
    }

    @HostBinding('class') get iconStyle() {
        return {
            [`bazis-icon-${this.size()}`]: !!this.size(),
            ['bazis-icon-gradient']: this.useGradient(),
        };
    }

    @HostBinding('ariaHidden') get ariaHidden() {
        return true;
    }

    readonly svgContent$: Signal<Observable<string>> = computed(() =>
        this.iconService.loadIcon$(this.getUrl()),
    );

    ngOnInit(): void {
        const host = this.el.nativeElement;
        if (host && host.shadowRoot) {
            const shadowRoot = host.shadowRoot;
            const styles = shadowRoot.querySelectorAll('style');
            styles.forEach((style) => style.remove());
            shadowRoot.adoptedStyleSheets = [sharedStylesheet];
        }
    }

    private getUrl(): string {
        let url = this.getSrc(this.src());
        if (url) {
            return url;
        }
        url = this.getName(this.name(), this.icon());
        if (url) {
            return this.getNamedUrl(url);
        }
        if (this.icon()) {
            url = this.getSrc(this.icon());
            if (url) {
                return url;
            }
        }
        return null;
    }

    private getNamedUrl(iconName: string) {
        return this.getAssetPath(`icons/${iconName}.svg`);
    }

    private getName(iconName: string, icon: string): string {
        if (!iconName && icon && !this.isSrc(icon)) {
            iconName = icon;
        }
        if (this.iconService.isStr(iconName)) {
            iconName = iconName.toLowerCase();
        }
        if (!this.iconService.isStr(iconName) || iconName.trim() === '') {
            return null;
        }
        // only allow alpha characters and dash
        const invalidChars = iconName.replace(/[a-z]|-|\d/gi, '');
        if (invalidChars !== '') {
            return null;
        }
        return iconName;
    }

    private getSrc(src): string {
        if (this.iconService.isStr(src)) {
            src = src.trim();
            if (this.isSrc(src)) {
                return src;
            }
        }
        return null;
    }

    private isSrc(str): boolean {
        return str.length > 0 && /(\/|\.)/.test(str);
    }

    private getAssetPath(path) {
        const assetUrl = new URL(path, `${window.location.origin}/assets/`);
        return assetUrl.origin !== window.location.origin ? assetUrl.href : assetUrl.pathname;
    }
}
